import { motion } from 'motion/react'
import { SEO } from '@/lib/seo'
import { Badge } from '@/components/ui'
import { companyInfo } from '@/data/company'

export function Terms() {
  return (
    <>
      <SEO
        title="Terms & Conditions"
        description="UC Advisors Terms and Conditions - Read our terms of service, managed services agreements, and usage policies."
      />

      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <Badge className="mb-6">Legal</Badge>
            <h1 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-8">
              Terms & Conditions
            </h1>

            <div className="prose prose-invert prose-slate max-w-none">
              <p className="text-slate-300 text-lg mb-8">
                Last updated: March 2026
              </p>

              <div className="space-y-8">
                {/* 1. Agreement to Terms */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    1. Agreement to Terms
                  </h2>
                  <p className="text-slate-400 mb-4">
                    These Terms and Conditions are entered into with UC Advisors, a registered DBA of UC Advisers LLC ("we," "us," or "our"). By accessing and using the UC Advisors website, client portal, assessments, forms, SMS communications, services, or by engaging UC Advisors for consulting or managed services, you ("Client," "you," or "your") agree to be bound by these Terms and Conditions ("Agreement"). If you do not agree to these terms, please do not use our website, portal, or services.
                  </p>
                  <p className="text-slate-400">
                    These Terms and Conditions, together with any service agreements, statements of work, order forms, onboarding portal terms, or managed service addenda, constitute the entire agreement between you and UC Advisors regarding the subject matter herein.
                  </p>
                </section>

                {/* 2. Definitions */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    2. Definitions
                  </h2>
                  <ul className="list-disc list-inside text-slate-400 space-y-3">
                    <li><strong className="text-white">"Services"</strong> means all consulting, brokerage, assessment, and managed services provided by UC Advisors, including but not limited to technology consulting, vendor evaluation, implementation support, and UC Care managed services.</li>
                    <li><strong className="text-white">"UC Care"</strong> means UC Advisors' managed services program providing ongoing support, monitoring, and maintenance for unified communications and contact center solutions.</li>
                    <li><strong className="text-white">"Client"</strong> means any individual or entity that engages UC Advisors for Services.</li>
                    <li><strong className="text-white">"Confidential Information"</strong> means any non-public information disclosed by either party, including business plans, technical data, customer information, and proprietary methodologies.</li>
                    <li><strong className="text-white">"Third-Party Vendors"</strong> means technology providers whose products or services UC Advisors recommends or coordinates, including but not limited to Zoom, Cisco, Microsoft, RingCentral, Genesys, Five9, and 8x8.</li>
                  </ul>
                </section>

                {/* 3. Services Description */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    3. Services
                  </h2>
                  <p className="text-slate-400 mb-4">
                    UC Advisors provides technology consulting and brokerage services, acting as an independent advisor to help organizations optimize their communications and technology infrastructure. Our services include:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-4">
                    <li>Technology consulting and strategic advisory services</li>
                    <li>Vendor-agnostic solution recommendations for UCaaS, CCaaS, network, SD-WAN, and cybersecurity</li>
                    <li>Free technology assessments (Cybersecurity, Cloud, Network, Data Analytics, AI, UCaaS, Contact Center)</li>
                    <li>Implementation planning and project coordination</li>
                    <li>UC Care managed services (ongoing support, monitoring, and maintenance)</li>
                    <li>Third-party vendor coordination and contract negotiation assistance</li>
                  </ul>
                  <p className="text-slate-400">
                    UC Advisors acts as a technology broker and advisor, not as a direct vendor of communications or technology products. Services provided by Third-Party Vendors are subject to those vendors' separate terms and conditions.
                  </p>
                </section>

                {/* 4. UC Care Managed Services */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    4. UC Care Managed Services
                  </h2>
                  <p className="text-slate-400 mb-4">
                    UC Care is UC Advisors' managed services program providing ongoing support for your unified communications and contact center environment. The following terms apply specifically to UC Care subscriptions:
                  </p>
                  
                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">4.1 Subscription Term</h3>
                  <p className="text-slate-400 mb-4">
                    <strong className="text-accent-400">UC Care subscriptions are provided on a month-to-month basis.</strong> There is no long-term commitment required. Services continue on a monthly basis until terminated by either party.
                  </p>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">4.2 Cancellation</h3>
                  <p className="text-slate-400 mb-4">
                    <strong className="text-accent-400">Either party may cancel UC Care services with thirty (30) days written notice.</strong> Written notice must be sent via email to support@ucadvisors.com or by mail to our Sacramento office. Upon cancellation:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-4">
                    <li>Services will continue through the end of the 30-day notice period</li>
                    <li>Client remains responsible for payment through the notice period</li>
                    <li>UC Advisors will provide reasonable transition assistance</li>
                    <li>Client data and access credentials will be returned or securely destroyed</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">4.3 Service Scope</h3>
                  <p className="text-slate-400 mb-4">
                    UC Care services include monitoring, support, and maintenance as described in the applicable service description. Specific service levels, response times, and included features are documented in the UC Care service agreement provided at enrollment.
                  </p>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">4.4 Client Responsibilities</h3>
                  <p className="text-slate-400 mb-4">
                    To receive UC Care services, Client agrees to:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2">
                    <li>Provide necessary access to systems and environments</li>
                    <li>Designate authorized contacts for support requests</li>
                    <li>Provide accurate and complete information about their environment</li>
                    <li>Maintain valid licenses for all Third-Party Vendor products</li>
                    <li>Cooperate with UC Advisors in the delivery of services</li>
                  </ul>
                </section>

                {/* 5. Payment Terms */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    5. Payment Terms
                  </h2>
                  
                  <h3 className="text-lg font-semibold text-white mt-4 mb-3">5.1 Billing</h3>
                  <p className="text-slate-400 mb-4">
                    UC Care and other recurring services are billed monthly in advance. Consulting and project-based services are billed as specified in the applicable statement of work or order form.
                  </p>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">5.2 Payment Due</h3>
                  <p className="text-slate-400 mb-4">
                    Payment is due within thirty (30) days of invoice date unless otherwise specified. We accept payment via ACH, wire transfer, check, or major credit cards.
                  </p>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">5.3 Late Payment</h3>
                  <p className="text-slate-400 mb-4">
                    <strong className="text-accent-400">Overdue balances are subject to a late payment fee of 1.5% per month (18% annually)</strong> on the outstanding balance, or the maximum rate permitted by law, whichever is less. Client is also responsible for reasonable collection costs, including attorney fees, if collection action becomes necessary.
                  </p>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">5.4 Service Suspension</h3>
                  <p className="text-slate-400">
                    UC Advisors reserves the right to suspend services if payment is more than thirty (30) days overdue. We will provide at least seven (7) days written notice before suspension. Suspended services may be reinstated upon payment of all outstanding balances and applicable late fees.
                  </p>
                </section>

                {/* 6. Confidentiality */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    6. Confidentiality
                  </h2>
                  <p className="text-slate-400 mb-4">
                    Both parties agree to protect the Confidential Information of the other party with the same degree of care used to protect their own confidential information, but no less than reasonable care.
                  </p>
                  <p className="text-slate-400 mb-4">
                    Confidential Information shall not include information that: (a) is or becomes publicly available through no fault of the receiving party; (b) was rightfully in the receiving party's possession prior to disclosure; (c) is rightfully obtained from a third party without restriction; or (d) is independently developed without use of the disclosing party's Confidential Information.
                  </p>
                  <p className="text-slate-400">
                    Confidentiality obligations survive termination of this Agreement for a period of three (3) years.
                  </p>
                </section>

                {/* 7. Intellectual Property */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    7. Intellectual Property
                  </h2>
                  <p className="text-slate-400 mb-4">
                    All content on the UC Advisors website, including text, graphics, logos, images, and software, is the property of UC Advisors or its content suppliers and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
                  </p>
                  <p className="text-slate-400">
                    UC Advisors retains ownership of all proprietary methodologies, tools, frameworks, and processes developed prior to or independent of any client engagement. Work product created specifically for a Client becomes Client's property upon full payment.
                  </p>
                </section>

                {/* 8. Third-Party Vendors */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    8. Third-Party Vendors
                  </h2>
                  <p className="text-slate-400 mb-4">
                    UC Advisors acts as an independent technology broker and advisor. We recommend and coordinate services from Third-Party Vendors but are not the direct provider of those technology products or services.
                  </p>
                  <p className="text-slate-400 mb-4">
                    Client's use of Third-Party Vendor products and services is governed by those vendors' separate terms of service, service level agreements, and privacy policies. UC Advisors is not responsible for:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-4">
                    <li>Service outages or performance issues caused by Third-Party Vendors</li>
                    <li>Changes to Third-Party Vendor pricing, features, or terms</li>
                    <li>Third-Party Vendor data handling practices</li>
                    <li>Direct disputes between Client and Third-Party Vendors</li>
                  </ul>
                  <p className="text-slate-400">
                    UC Advisors will use commercially reasonable efforts to assist in resolving issues with Third-Party Vendors as part of our advisory services.
                  </p>
                </section>

                {/* 9. Disclaimer of Warranties */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    9. Disclaimer of Warranties
                  </h2>
                  <p className="text-slate-400 mb-4">
                    EXCEPT AS EXPRESSLY SET FORTH HEREIN, UC ADVISERS PROVIDES SERVICES "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                  </p>
                  <p className="text-slate-400 mb-4">
                    UC Advisors does not warrant that:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2">
                    <li>Services will be uninterrupted or error-free</li>
                    <li>Third-Party Vendor products will meet specific performance requirements</li>
                    <li>Any particular outcome or result will be achieved</li>
                    <li>Cost savings or efficiency improvements are guaranteed</li>
                  </ul>
                </section>

                {/* 10. Limitation of Liability */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    10. Limitation of Liability
                  </h2>
                  <p className="text-slate-400 mb-4">
                    TO THE FULLEST EXTENT PERMITTED BY LAW, UC ADVISERS' TOTAL LIABILITY FOR ANY CLAIMS ARISING OUT OF OR RELATING TO THIS AGREEMENT OR THE SERVICES SHALL NOT EXCEED THE TOTAL FEES PAID BY CLIENT TO UC ADVISERS DURING THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
                  </p>
                  <p className="text-slate-400 mb-4">
                    IN NO EVENT SHALL UC ADVISERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, BUSINESS OPPORTUNITIES, OR GOODWILL, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                  </p>
                  <p className="text-slate-400">
                    These limitations apply regardless of the form of action, whether in contract, tort (including negligence), strict liability, or otherwise.
                  </p>
                </section>

                {/* 11. Indemnification */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    11. Indemnification
                  </h2>
                  <p className="text-slate-400">
                    Client agrees to indemnify, defend, and hold harmless UC Advisors and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorney fees) arising out of or relating to: (a) Client's breach of this Agreement; (b) Client's violation of applicable laws or regulations; (c) Client's use of Third-Party Vendor services; or (d) any claim that Client's materials or data infringe the rights of a third party.
                  </p>
                </section>

                {/* 12. Dispute Resolution */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    12. Dispute Resolution
                  </h2>
                  <p className="text-slate-400 mb-4">
                    <strong className="text-white">Binding Arbitration:</strong> Any dispute, claim, or controversy arising out of or relating to this Agreement or the Services shall be resolved by binding arbitration administered by the American Arbitration Association ("AAA") in accordance with its Commercial Arbitration Rules.
                  </p>
                  <p className="text-slate-400 mb-4">
                    Arbitration shall take place in Sacramento County, California. The arbitrator's decision shall be final and binding, and judgment on the award may be entered in any court of competent jurisdiction.
                  </p>
                  <p className="text-slate-400 mb-4">
                    <strong className="text-white">Class Action Waiver:</strong> YOU AND UC ADVISERS AGREE THAT EACH PARTY MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
                  </p>
                  <p className="text-slate-400">
                    Each party shall bear its own costs and attorney fees in any arbitration, unless the arbitrator determines that a claim was frivolous, in which case the losing party shall pay the prevailing party's reasonable costs and fees.
                  </p>
                </section>

                {/* 13. Governing Law */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    13. Governing Law
                  </h2>
                  <p className="text-slate-400">
                    This Agreement shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions. Subject to the arbitration provisions above, any legal action not subject to arbitration shall be brought exclusively in the state or federal courts located in Sacramento County, California.
                  </p>
                </section>

                {/* 14. Changes to Terms */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    14. Changes to Terms
                  </h2>
                  <p className="text-slate-400">
                    We reserve the right to modify these Terms and Conditions at any time. Changes will be effective upon posting on the website. For active service agreements, we will provide at least thirty (30) days notice of material changes. Your continued use of the website or Services after changes constitutes acceptance of the modified terms.
                  </p>
                </section>

                {/* 15. Severability */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    15. Severability
                  </h2>
                  <p className="text-slate-400">
                    If any provision of this Agreement is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that this Agreement shall otherwise remain in full force and effect.
                  </p>
                </section>

                {/* 16. Contact Us */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    16. Contact Us
                  </h2>
                  <p className="text-slate-400 mb-4">
                    If you have any questions about these Terms and Conditions, please contact us at:
                  </p>
                  <div className="text-slate-300 space-y-1">
                    <p className="font-semibold">{companyInfo.name}</p>
                    <p>{companyInfo.locations[0].address}</p>
                    <p>{companyInfo.locations[0].city}, {companyInfo.locations[0].state}</p>
                    <p>Email: {companyInfo.email}</p>
                    <p>Phone: {companyInfo.phone}</p>
                  </div>
                </section>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}
